import { createFileRoute, Link } from "@tanstack/react-router";
import { STATS_TEXT } from "@/lib/stats";
import { useEffect } from "react";
import { SiteLayout } from "@/components/site/Layout";
import { trackFormView } from "@/lib/analytics";
import { useGhlSubmitConfirm } from "@/lib/confirm-submission";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { BRAND } from "@/lib/brand";
import { DirectAnswer, FAQSection, faqSchema } from "@/components/site/AnswerBlock";
import { CONTACT_ANSWER, CONTACT_FAQS } from "@/lib/aeo";

const URL = "https://joinkcn.com/contact-us";
const FORM_ID = "k8voTA3U8DlaGhEKlC5L";
const FORM_SRC = `https://link.agent-moves.com/widget/form/${FORM_ID}`;
const SCRIPT_SRC = "https://link.agent-moves.com/js/form_embed.js";

const ADDRESS = "15333 N. Pima Road, Suite 130, Scottsdale, AZ 85260";
const PHONE = "(512) 318-2627";
const PHONE_TEL = "+15123182627";

export const Route = createFileRoute("/contact-us")({
  head: () => ({
    meta: [
      { title: "Contact Kristan Cole Network - Talk to Our Team" },
      {
        name: "description",
        content:
          "Contact Kristan Cole and the KCN leadership team, based near Keller Williams Scottsdale. Ask about joining KCN, our markets, and what a move would look like.",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Contact the Kristan Cole Network" },
      {
        property: "og:description",
        content:
          "Reach our leadership team in Scottsdale, AZ. We'll tell you honestly whether KCN is the right fit.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          url: URL,
          mainEntity: {
            "@type": "RealEstateAgent",
            name: BRAND.name,
            url: "https://joinkcn.com/",
            telephone: PHONE_TEL,
            address: {
              "@type": "PostalAddress",
              streetAddress: "15333 N. Pima Road, Suite 130",
              addressLocality: "Scottsdale",
              addressRegion: "AZ",
              postalCode: "85260",
              addressCountry: "US",
            },
            contactPoint: [
              {
                "@type": "ContactPoint",
                telephone: PHONE_TEL,
                contactType: "recruiting",
                areaServed: "US",
                availableLanguage: "English",
              },
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://joinkcn.com/" },
            { "@type": "ListItem", position: 2, name: "Contact Us", item: URL },
          ],
        }),
      },
      faqSchema(CONTACT_FAQS, URL),
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  useGhlSubmitConfirm("contact", "/contact-us");
  useEffect(() => {
    trackFormView("contact", "/contact-us");
    if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  return (
    <SiteLayout>
      <section className="pt-36 pb-14 border-b border-border">
        <div className="container-edge">
          <p className="eyebrow mb-6">Contact Us</p>
          <h1 className="headline-xl max-w-5xl">
            Start the conversation.
            <br />
            <span className="serif-italic text-primary">No pressure either way.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-foreground/80 leading-relaxed">
            Our leadership team looks forward to speaking with you. Tell us where
            your business is today and we'll be straight with you about whether
            KCN is the right move.
          </p>
        </div>
      </section>

      {/* AEO quotable answer */}
      <Section tone="darker" className="py-14 md:py-16">
        <div className="max-w-3xl border-l-2 border-primary pl-6">
          <p className="eyebrow mb-4">In short</p>
          <p className="text-lg md:text-xl text-foreground/90 leading-relaxed">
            You can reach the Kristan Cole Network at {PHONE}, or at our
            Scottsdale office at {ADDRESS}. KCN is a national network of Keller
            Williams brokerages led by Kristan Cole, recruiting growing agents
            across {STATS_TEXT.markets} markets nationwide.
          </p>
        </div>
      </Section>

      <Section tone="dark">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow>Reach us</SectionEyebrow>
            <h2 className="headline-md mb-10">Talk to a real person.</h2>

            <dl className="space-y-8">
              <div>
                <dt className="eyebrow mb-2">Phone</dt>
                <dd>
                  <a href={`tel:${PHONE_TEL}`} className="font-display text-2xl hover:text-primary">
                    {PHONE}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="eyebrow mb-2">Office</dt>
                <dd className="text-foreground/85 leading-relaxed">
                  15333 N. Pima Road, Suite 130
                  <br />
                  Scottsdale, AZ 85260
                </dd>
              </div>
              <div>
                <dt className="eyebrow mb-2">Markets</dt>
                <dd className="text-foreground/85 leading-relaxed">
                  {STATS_TEXT.markets} markets nationwide and growing.{" "}
                  <Link to="/kcn-worldwide" className="text-primary hover:underline">
                    See where we are
                  </Link>
                  .
                </dd>
              </div>
              <div>
                <dt className="eyebrow mb-2">Prefer to look first?</dt>
                <dd className="text-foreground/85 leading-relaxed">
                  <Link to="/meet-the-team" className="text-primary hover:underline">
                    Meet the leadership team
                  </Link>{" "}
                  or{" "}
                  <Link to="/value" className="text-primary hover:underline">
                    see what KCN covers
                  </Link>
                  .
                </dd>
              </div>
            </dl>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-border bg-surface p-1">
              <iframe
                src={FORM_SRC}
                style={{ width: "100%", border: "none", display: "block" }}
                id={`inline-contact-${FORM_ID}`}
                data-layout='{"id":"INLINE"}'
                data-trigger-type="alwaysShow"
                data-activation-type="alwaysActivated"
                data-deactivation-type="neverDeactivate"
                data-form-name="Contact KCN"
                data-height="700"
                data-layout-iframe-id={`inline-contact-${FORM_ID}`}
                data-form-id={FORM_ID}
                title="Contact the Kristan Cole Network"
                height={700}
                className="min-h-[600px] md:min-h-[700px] w-full"
              />
            </div>
          </div>
        </div>
      </Section>

      <DirectAnswer question={CONTACT_ANSWER.question} answer={CONTACT_ANSWER.answer} />

      <FAQSection
        faqs={CONTACT_FAQS}
        eyebrow="Before you reach out"
        heading="Common questions."
      />
    </SiteLayout>
  );
}
